import React, {useEffect, useState} from "react";
import {AccountService} from "../../service/AccountService";
import {emailValidator, formValid, getKeyValue, isCampaignManufacturer, isCampaignSeller, user} from "../../utils/Utils";

import {Button} from "primereact/button";
import {AuthService} from "../../service/AuthService";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {MultiSelect} from "primereact/multiselect";
import {InputSwitch} from "primereact/inputswitch";
import {Tooltip} from "primereact/tooltip";

// One-line English defaults for each notification type's tooltip (Turkish lives in i18n: account.notif.desc.<type>).
const DESCRIPTIONS = {
    summary_competitor_price_list: "A side-by-side comparison of your products against current competitor prices, showing the lowest, average and highest price and where you stand for each product.",
    price_comparison_summary: "A short digest summarizing your price comparison results across all campaigns at a glance.",
    category_price_comparison: "Compares the catalogue of your base campaigns with the competing chains' prices, with a category level summary.",
    n_cheapest_seller_price_comparison: "Lists the cheapest sellers for each product so you can see the lowest prices in the market.",
    price_comparison_marketplace_sellers_vertical: "Marketplace sellers' prices for your products, delivered as a vertical-layout Excel file.",
    price_comparison_marketplace_sellers_horizontal: "Marketplace sellers' prices for your products, delivered as a horizontal-layout Excel file.",
    competitor_price_list: "A detailed Excel file listing all tracked competitor prices for your products.",
    detailed_product_list: "A detailed export of your products with all of their fields and current data.",
    price_stock_changes: "Alerts you when prices or stock change on your products or their competitors.",
    stock_out: "Alerts you after each price check when a tracked seller runs out of stock.",
    stock_in: "Alerts you after each price check when a seller that was out of stock has it again.",
    price_violation: "Notifies you when a seller breaks your MAP/MRP rules by selling below or above your set price.",
    unauthorized_sellers: "Flags sellers that are offering your products without authorization.",
    inactive_urls: "Reports product or competitor links that have become unreachable or inactive.",
    suggestion_summary: "A summary of dynamic pricing suggestions generated for your products.",
    products_without_code: "Lists your products that are missing an identifier such as SKU or barcode.",
    new_sellers: "Notifies you when a new seller starts offering your products.",
    applied_dynamic_pricing: "Reports the prices that were automatically updated by your dynamic pricing rules.",
};

// Group the 17 notification types into 3 user-facing categories.
const CATEGORIES = [
    { key: 'comparison', labelKey: 'account.notif.cat.comparison', defaultLabel: 'Price comparison reports',
      icon: 'pi-chart-bar', iconBg: '#E6F1FB', iconColor: '#185FA5',
      types: ['summary_competitor_price_list','price_comparison_summary','category_price_comparison',
              'n_cheapest_seller_price_comparison',
              'price_comparison_marketplace_sellers_vertical','price_comparison_marketplace_sellers_horizontal',
              'competitor_price_list','detailed_product_list'] },
    { key: 'alerts', labelKey: 'account.notif.cat.alerts', defaultLabel: 'Changes & alerts',
      icon: 'pi-bell', iconBg: '#FAEEDA', iconColor: '#BA7517',
      types: ['price_stock_changes','stock_out','stock_in','price_violation','unauthorized_sellers','new_sellers','inactive_urls'] },
    { key: 'quality', labelKey: 'account.notif.cat.quality', defaultLabel: 'Suggestions & data quality',
      icon: 'pi-check-circle', iconBg: '#E1F5EE', iconColor: '#0F6E56',
      types: ['suggestion_summary','products_without_code','applied_dynamic_pricing'] },
];

const Notification = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const {campaigns} = React.useContext(CampaignContext);

    const defaultTrue = { active:true, email:user().email, campaigns:null, period: null};
    const defaultFalse = { active:false, email:user().email, campaigns:null, period: null};

    const types = [
        {value:'summary_competitor_price_list', label:t('dashboard.price_comparison')},
        {value:'price_comparison_summary', label:t('dashboard.price_comparison_summary')},
        {value:'category_price_comparison', label:t('dashboard.category_price_comparison')},
        {value:'n_cheapest_seller_price_comparison', label:t('dashboard.n_cheapest_seller_price_comparison')},
        {value:'price_comparison_marketplace_sellers_vertical', label:t('dashboard.marketplace_sellers_vertical')},
        {value:'price_comparison_marketplace_sellers_horizontal', label:t('dashboard.marketplace_sellers_horizontal')},
        {value:'competitor_price_list', label:t('dashboard.excel_competitor_list')},
        {value:'detailed_product_list', label:t('dashboard.detailed_product_list')},
        {value:'price_stock_changes', label:t('dashboard.changes')},
        {value:'stock_out', label:t('dashboard.stock_out')},
        {value:'stock_in', label:t('dashboard.stock_in')},
        {value:'price_violation', label:t('dashboard.violations')},
        {value:'unauthorized_sellers', label:t('dashboard.unauthorized_sellers')},
        {value:'inactive_urls', label:t('dashboard.inactive_urls')},
        {value:'suggestion_summary', label:t('dashboard.suggestion_summary')},
        {value:'products_without_code', label:t('dashboard.products_without_code')},
        {value:'new_sellers', label:t('dashboard.new_sellers')},
        {value:'applied_dynamic_pricing', label:t('dashboard.dynamic_pricing')}
    ];

    const [currentUser, setCurrentUser] = useState(user());
    const [notification, setNotification] = useState(null);
    const [newNotification, setNewNotification] = useState({email:""});
    const [formErrors, setFormErrors] = useState({email:""});
    const [activeEmail, setActiveEmail] = useState(currentUser.email);
    const [showAddRecipient, setShowAddRecipient] = useState(false);

    const accountService = new AccountService();

    useEffect(()=>{
        let nots = {};
        if(currentUser){
            let emails = [];
            emails.push(currentUser.email);
            if (currentUser.notification)
                Object.keys(currentUser.notification).forEach(n=>{
                    return currentUser.notification[n].forEach(n1=> {
                        if (emails.findIndex(email=> email === n1.email) === -1)
                            emails.push(n1.email);
                    });
                });
            types.forEach(t => {
                if (!campaigns.some(c=> isCampaignManufacturer(c)) && (t.value === "price_violation" || t.value === "unauthorized_sellers"))
                    return;
                else if (!campaigns.some(c=> isCampaignSeller(c)) && t.value === "applied_dynamic_pricing" )
                    return;
                // Opened per customer: hide the type entirely rather than
                // offering a report the backend would refuse to send.
                else if (t.value === "category_price_comparison" &&
                    !(currentUser.settings && currentUser.settings.category_price_comparison_enabled))
                    return;

                let defaultNot = defaultFalse;
                if (t.value === "summary_competitor_price_list" || t.value === "price_stock_changes" || t.value === "price_violation" || t.value === "applied_dynamic_pricing")
                    defaultNot = defaultTrue;

                let not = [];
                emails.forEach(email =>{
                    if (currentUser.notification && currentUser.notification[t.value] && currentUser.notification[t.value].length !== 0 &&
                        currentUser.notification[t.value].findIndex(n=> n.email === email) !== -1)
                        not.push({...defaultNot, ...currentUser.notification[t.value].find(n=> n.email === email)});
                    else
                        not.push({...defaultNot, email: email});
                });
                nots[t.value] = not;
            });
        }
        setNotification(nots);
    },[currentUser])

    const add = () => {
        checkErrors(newNotification).then((formErrors)=>{
            if (formValid(formErrors)) {
                if (notifications().some(s => s.email === newNotification.email))
                {
                    toast.current.show({severity: 'warn', summary: t('user.notification.exist'), detail: ""});
                    return;
                }
                types.forEach(t=>{
                    if (!notification[t.value])
                        notification[t.value] = []
                    notification[t.value].push({...defaultFalse, email:newNotification.email});
                })
                setActiveEmail(newNotification.email);
                setNewNotification({email:""});
                setShowAddRecipient(false);
                save(notification);
            }
            else
                setFormErrors(formErrors);
        })

    };

    const deleteMail = (rowData) => {

        types.forEach(t=>{
            notification[t.value] && notification[t.value].forEach(n=> {
                notification[t.value] = notification[t.value].filter(n => n.email !== rowData.email);
            });
        });

        save(notification);
    };

    const changeAndSave = (e, rowData) => {

        const { key, value } = getKeyValue(e);

        if (notification[rowData.type])
            notification[rowData.type][notification[rowData.type].findIndex(n => n.email === rowData.email)][key] = value;
        else
            notification[rowData.type].push({...rowData, key: value});

        setNotification({...notification});
        save(notification);
    };

    const onChange = (e) =>{

        const { key, value } = getKeyValue(e);

        setFormErrors({...formErrors, [key]: checkError(key, value)});
        setNewNotification({...newNotification, [key] : value});
    };

    const errorClass = (key) => {
        return formErrors[key] && formErrors[key].length > 0 ? "p-error" : null
    };

    const checkError = (key, value) => {
        let errorText = "";

        if (key === "email") {
            errorText =  value.length > 0 ? emailValidator(value, t) : t('validations.invalid_email');
        }

        return errorText;
    }

    const checkErrors = async (notification) => {

        let errors = { ...formErrors };

        Object.entries(notification).forEach(([key, value]) => {
            errors[key] = checkError(key, value);
        });

        return errors;
    };

    const notifications = () => {
        if (!notification)
            return;

        let result = [];
        types.forEach(t=> {
            notification[t.value]?.forEach(obj=> {
                result.push({ ...obj, type: t.value });
            });
        });

        return result;
    };

    const save =(notification) => {

        currentUser.notification = notification;
        accountService.updateMyAccount(currentUser).then(user_updated=>{
            setNotification({...notification});
            AuthService.setUser(user_updated);
            setCurrentUser(user_updated);
            toast.current.show({severity: 'success', summary: t('account.save_notification'), detail: t('message_detail.successful')});
        }).catch(error =>{
            setError(error);
            setError(null);
        });
    };

    // distinct recipient emails: owner first, then any added in notification config
    const recipientEmails = () => {
        const set = [currentUser.email];
        Object.keys(notification || {}).forEach(type =>
            (notification[type] || []).forEach(n => { if (!set.includes(n.email)) set.push(n.email); }));
        return set;
    };
    // rows for the active recipient, indexed by type, only for types currently present
    const rowFor = (type) => (notification?.[type] || []).find(n => n.email === activeEmail);
    const typeLabel = (type) => types.find(x => x.value === type)?.label;
    const typeAllowed = (type) => types.some(x => x.value === type); // respects manufacturer/seller filtering in useEffect
    const typeDesc = (type) => t('account.notif.desc.' + type, DESCRIPTIONS[type] || "");

    return (
        <div className="acc-section">
            <Tooltip key={`acc-ntip-${activeEmail}-${notification ? Object.keys(notification).length : 0}`} target=".acc-ntip" position="top" style={{ maxWidth: 320 }} />
            <p className="acc-section-title">{t('account.headers.notification', 'Notifications')}</p>
            <p className="acc-section-sub">{t('account.notif.sub', 'Choose which report each recipient gets, and for which campaigns.')}</p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 8 }}>
                {recipientEmails().map(email => (
                    <button type="button" key={email}
                            className={"acc-chip" + (email === activeEmail ? " is-active" : "")}
                            onClick={() => setActiveEmail(email)}>
                        {email === currentUser.email ? <><i className="pi pi-user" /> {t('account.notif.you', 'You')}</> : email}
                    </button>
                ))}
                <button type="button" className="acc-chip acc-chip--add"
                        onClick={() => setShowAddRecipient(s => !s)}>
                    <i className="pi pi-plus" /> {t('account.notif.add_recipient', 'Add recipient')}
                </button>
            </div>

            {showAddRecipient && (
                <>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                        <InputText id="email" name="email" placeholder={t('user.notification.email', 'recipient@email.com')}
                                   value={newNotification.email} onChange={onChange} className={errorClass("email")} style={{ maxWidth: 260 }} />
                        <Button label={t('actions.add', 'Add recipient')} icon="pi pi-plus"
                                className="p-button-outlined p-button-success" onClick={add} />
                    </div>
                    {errorClass("email") && <Message severity="error" text={formErrors.email} />}
                </>
            )}

            {activeEmail !== currentUser.email &&
                <div style={{ textAlign: 'right', marginBottom: 4 }}>
                    <Button label={t('actions.remove', 'Remove recipient')} icon="pi pi-trash"
                            className="p-button-text p-button-danger"
                            onClick={() => { deleteMail({ email: activeEmail }); setActiveEmail(currentUser.email); }} />
                </div>}

            <div className="acc-ncards">
                {CATEGORIES.map(cat => {
                    const catTypes = cat.types.filter(typeAllowed);
                    const rows = catTypes.map(type => rowFor(type)).filter(Boolean);
                    if (rows.length === 0) return null;
                    return (
                        <div className="acc-ncard" key={cat.key}>
                            <div className="acc-ncard-head">
                                <span className="acc-nico" style={{ background: cat.iconBg, color: cat.iconColor }}>
                                    <i className={"pi " + cat.icon} />
                                </span>
                                <span className="acc-nct">{t(cat.labelKey, cat.defaultLabel)}</span>
                            </div>
                            {catTypes.map(type => {
                                const row = rowFor(type);
                                if (!row) return null;
                                return (
                                    <div className="acc-toggle-row" key={type}>
                                        <span className="acc-label">{typeLabel(type)}<i className="pi pi-info-circle acc-ntip" data-pr-tooltip={typeDesc(type)} data-pr-position="top" style={{ marginLeft: 6 }} /></span>
                                        {row.active &&
                                            <MultiSelect value={row.campaigns}
                                                         options={campaigns.slice().sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()))}
                                                         optionLabel="name" optionValue="id" name="campaigns"
                                                         placeholder={t('user.notification.choose_campaigns', 'All campaigns')}
                                                         onChange={(e) => changeAndSave(e, { ...row, type })}
                                                         style={{ maxWidth: 200 }} />}
                                        <InputSwitch checked={row.active === true} name="active"
                                                     onChange={(e) => changeAndSave(e, { ...row, type })} />
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
export default Notification
